import { BusinessPolicy, BusinessResult, BusinessResultType, BusinessValidation, BusinessValidationType } from '../models/policy-business.model';
import { isValidIBAN, isValidBBAN, getIBANLength } from './bank-account-validation';
import { isValidEmail } from './shared-validation';

export function businessCheckSvsLines(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;

    const originalNumOfLines = policy.Svs.svsLines.length;

    policy.Svs.svsLines = policy.Svs.svsLines.filter(item => item.amount > 0 || item.isImportFromDacca);

    policy.Svs.svsTotal = policy.Svs.svsLines.filter((line) => !line.deleted).reduce((prev, next) => prev + +next.amount, 0);
    if (!policy.Svs.svsTotal) { policy.Svs.svsTotal = 0; }

    if (originalNumOfLines !== policy.Svs.svsLines.length) {
        result.resultType = BusinessResultType.error;
        result.messages = ['validation.svs.line.amount.must.be.grater.than.0'];
    }

    if (result.resultType === BusinessResultType.ok) {
        policy.Svs.currentLine.description = '';
    }

    policy.Svs.currentLine.amount = 0;

    result.data = policy;
    return result;
}

export function businessCheckSvsLinesNumber(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    if (policy.Svs.svsLines.length !== 1 || !policy.Svs.svsLines[0].isImportFromDacca) {
        const svsTotal = policy.Svs.svsLines.filter((line) => !line.deleted).reduce((prev, next) => prev + +next.amount, 0);
        if (svsTotal === 0) {
            result.resultType = BusinessResultType.error;
            result.messages = ['validation.svs.atLeastOneSvsLineIsRequired'];
        }
    }

    result.data = policy;
    return result;
}

export function businessCheckSvsExcessApplicableAndUnderConditionTC(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;
    result.data.Svs.excessApplicableIsValid = true;
    result.data.Svs.underConditionIsValid = true;

    if (!policy.Svs.hasDaccaCaluclation && !policy.Svs.excessApplicableTCchecked) {
        result.resultType = BusinessResultType.error;
        result.messages.push('validation.svs.since.there.is.no.dacca.calculation.excess.applicable.should.be.checked');
        result.data.Svs.excessApplicableIsValid = false;
    }

    if (!policy.Svs.hasDaccaCaluclation && !policy.Svs.underConditionTCchecked) {
        result.resultType = BusinessResultType.error;
        result.messages.push('validation.svs.since.there.is.no.dacca.calculation.under.condition.should.be.checked');
        result.data.Svs.underConditionIsValid = false;
    }

    result.data = policy;
    return result;
}

export function businessCheckSvsIsDamageCauseValid(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;
    result.data.Svs.damageCauseRemarkIsValid = true;

    if (policy.Svs.damageCauseRemark !== null && policy.Svs.damageCauseRemark.trim() === '') {
        result.resultType = BusinessResultType.error;
        result.messages = ['validation.svs.damage.cause.has.to.be.filled.in.if.about.to.finish.or.send'];
        result.data.Svs.damageCauseRemarkIsValid = false;
    }

    return result;
}

export function checkEmptyIban(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
    const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;
    if (policy.Svs.iban == '') {
        result.resultType = BusinessResultType.error;
        result.messages.push('validation.iban.notValid');
        const validationError = { businessValidationType: BusinessValidationType.ibanSvs, errorMessage: 'validation.iban.notValid', hasError: true} as BusinessValidation;
        result.validations.push(validationError);
    }

    return result;
}

export function businessCheckSvsBankAccount(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;
    if (policy.Svs.iban) {
        const ibanValidation = isValidIBAN(policy.Svs.iban);
        if (ibanValidation.resultType === BusinessResultType.error) {
            result.resultType = BusinessResultType.error;
            result.messages.push(...ibanValidation.messages);
        }
    }
    if (policy.Svs.financialAccountNumber) {
        const bbanValidation = isValidBBAN(policy.Svs.financialAccountNumber);
        if (bbanValidation.resultType === BusinessResultType.error) {
            result.resultType = BusinessResultType.error;
            result.messages.push(...bbanValidation.messages);
        }
    }

    return result;
}

export function businessCheckSvsEmailAddresses(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;

    if (policy.Svs.sendEmailToPolicyHolder) {
        const emailValidation = isValidEmail(policy.Svs.emailPolicyHolder);
        if (emailValidation.resultType === BusinessResultType.error) {
            result.resultType = BusinessResultType.error;
            result.messages.push(...emailValidation.messages);
        }
    }

    if (policy.Svs.sendEmailToPersonalExpert) {
        const emailValidation = isValidEmail(policy.Svs.emailPersonalExpert);
        if (emailValidation.resultType === BusinessResultType.error) {
            result.resultType = BusinessResultType.error;
            result.messages.push(...emailValidation.messages);
        }
    }

    if (policy.Svs.sendEmailToCounterExpert) {
        const emailValidation = isValidEmail(policy.Svs.emailCounterExpert);
        if (emailValidation.resultType === BusinessResultType.error) {
            result.resultType = BusinessResultType.error;
            result.messages.push(...emailValidation.messages);
        }
    }

    if (policy.Svs.sendEmailToOther) {
        const emailValidation = isValidEmail(policy.Svs.emailOther);
        if (emailValidation.resultType === BusinessResultType.error) {
            result.resultType = BusinessResultType.error;
            result.messages.push(...emailValidation.messages);
        }
    }

    return result;
}

export function businessCheckSvsIsValid(policy: BusinessPolicy): BusinessResult {
    const damageCauseIsValid = businessCheckSvsIsDamageCauseValid(policy);
    const excessAndApplicableIsValid = businessCheckSvsExcessApplicableAndUnderConditionTC(damageCauseIsValid.data as BusinessPolicy);
    const linesAreValid = businessCheckSvsLines(excessAndApplicableIsValid.data as BusinessPolicy);
    const linesTotalValid = businessCheckSvsLinesNumber(linesAreValid.data as BusinessPolicy);
    const bankAccountIsValid = businessCheckSvsBankAccount(linesTotalValid.data as BusinessPolicy);
    const emailAddressesAreValid = businessCheckSvsEmailAddresses(bankAccountIsValid.data as BusinessPolicy);
    const businessIbanCheck = businessUpdateSvsIBAN(policy);
    const emptyIBAN = checkEmptyIban(policy);

    const allResultTypes: BusinessResultType[] = [];
    allResultTypes.push(damageCauseIsValid.resultType);
    allResultTypes.push(excessAndApplicableIsValid.resultType);
    allResultTypes.push(linesAreValid.resultType);
    allResultTypes.push(linesTotalValid.resultType);
    allResultTypes.push(bankAccountIsValid.resultType);
    allResultTypes.push(emailAddressesAreValid.resultType);
    allResultTypes.push(businessIbanCheck.resultType);
    allResultTypes.push(emptyIBAN.resultType);

    const validations: BusinessValidation[] = [];
    validations.push(...businessIbanCheck.validations);

    const hasErrors = allResultTypes.filter(x => x === BusinessResultType.error).length > 0;
    const hasWarnings = allResultTypes.filter(x => x === BusinessResultType.error).length > 0;

    const result: BusinessResult = {
    resultType: hasErrors
        ? BusinessResultType.error
        : hasWarnings
        ? BusinessResultType.warning
        : BusinessResultType.ok,
    messages: damageCauseIsValid.messages
        .concat(excessAndApplicableIsValid.messages)
        .concat(linesAreValid.messages)
        .concat(linesTotalValid.messages)
        .concat(bankAccountIsValid.messages)
        .concat(emailAddressesAreValid.messages)
        .concat(emptyIBAN.messages),
    data: bankAccountIsValid.data,
    validations: validations,
    };

    return result;
}

export function businessSvsValidateForSend(policy: BusinessPolicy): BusinessResult {
    // add additonal rules, like at least one line must be presented
    const validationResult = businessCheckSvsIsValid(policy);

    return validationResult;
}

export function businessUpdateSvsIBAN(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.ibanSvs,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;

    if (policy.Svs.iban.length === 0) { return result; }
    const ibanLength = getIBANLength(policy.Svs.iban);

    if (ibanLength === 0) {
        result.resultType = BusinessResultType.error;
        result.messages.push('validation.iban.notValid');
        policy.Svs.iban = '';
        const businessError = { businessValidationType: BusinessValidationType.ibanSvs, errorMessage: 'validation.iban.notValid', hasError: true} as BusinessValidation;
        result.validations.push(businessError);
    } else {
        const ibanValidation = isValidIBAN(policy.Svs.iban);
        result.resultType = ibanValidation.resultType;
        result.messages = ibanValidation.messages;
        policy.Svs.iban = policy.Svs.iban.slice(0, ibanLength);
        result.messages.forEach(item => {
            const error = { businessValidationType: BusinessValidationType.ibanSvs, errorMessage: item, hasError: true} as BusinessValidation;
            result.validations.push(error);
          });
    }

    return result;
}

export function businessUpdateSvsBBAN(policy: BusinessPolicy): BusinessResult {
    const validationInProgress: BusinessValidation = {
        businessValidationType: BusinessValidationType.none,
        errorMessage: '',
        hasError: false,
      };
      const result: BusinessResult = {
        resultType: BusinessResultType.ok,
        messages: [],
        data: null,
        validations: [validationInProgress],
      };
    result.data = policy;
    const bbanLength = 12;
    if (policy.Svs.financialAccountNumber.length >= bbanLength) {
        const bbanValidation = isValidBBAN(policy.Svs.financialAccountNumber);
        result.resultType = bbanValidation.resultType;
        result.messages = bbanValidation.messages;
        policy.Svs.financialAccountNumber = policy.Svs.financialAccountNumber.slice(0, bbanLength);
    }

    return result;
}
